<template>
  <transition name="modal">
    <div class="modal-mask" @click.self="handleClose">
      <div class="modal-wrapper">
        <div class="modal-dialog modal-dialog-scrollable">
          <div class="modal-content rounded-8">
            
            <ModalHeader 
              title="Generación de usuarios"
              @close="handleClose"
            />
            
            <ModalBody>
              <UserFilters
                :filters="filters"
                :show-filters="showFilters"
                @toggle-filters="showFilters = !showFilters"
                @update-filters="filters = $event"
                @filter="handleFilter"
                @clear="handleClearFilters"
              />

              <UsersDataTable
                :users="users"
                :headers="tableHeaders"
                :loading="loading"
                :pagination="pagination"
                :selected-users="selectedUsers"
                @update-selection="handleSelectionUpdate"
                @page-change="handlePageChange"
              />
            </ModalBody>
            
            <ModalFooter>
              <div class="d-flex justify-content-end gap-3 w-100">
                <button 
                  type="button" 
                  class="btn text-primary bg-utel-v2-secondary rounded-8" 
                  @click="handleClose"
                  :disabled="loading"
                >
                  Cancelar
                </button>
                
                <button 
                  type="button" 
                  class="btn text-white bg-utel rounded-8" 
                  @click="handleGenerate"
                  :disabled="!canGenerate"
                  v-html="generateButtonHtml"
                >
                </button>
              </div>
            </ModalFooter>
            
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ModalHeader from './components/ModalHeader.vue'
import ModalBody from './components/ModalBody.vue'
import ModalFooter from './components/ModalFooter.vue'
import UserFilters from './components/UserFilters.vue'
import UsersDataTable from './components/UsersDataTable.vue'

export default {
  name: 'ModalGenerateUsers',
  
  components: {
    ModalHeader,
    ModalBody,
    ModalFooter,
    UserFilters,
    UsersDataTable
  },

  props: {
    urlstudents: { type: String, required: true }
  },

  emits: ['close', 'reloadtable'],

  data() {
    return {
      users: [],
      selectedUsers: [],
      loading: false,
      generating: false,
      showFilters: false,
      
      filters: {
        firstname: '',
        lastname: '',
        email: '',
        position: '',
        area: '',
        fecha: ''
      },

      pagination: {
        page: 1,
        pageCount: 0,
        itemsPerPage: 10,
        totalItems: 0
      },

      tableHeaders: [
        { text: 'Nombres', value: 'firstname' },
        { text: 'Apellidos', value: 'lastname' },
        { text: 'Correo', value: 'email' },
        { text: 'Cargo', value: 'position' },
        { text: 'Área', value: 'area' },
        { text: 'Fecha de creación', value: 'created_at_convert_complete' }
      ]
    }
  },

  computed: {
    generateButtonText() {
      if (this.generating) {
        return 'Generando...'
      }
      return `Generar (${this.selectedUsers.length})`
    },

    generateButtonHtml() {
      if (this.generating) {
        return '<span class="spinner-border spinner-border-sm me-2"></span>Generando...'
      }
      return `Generar (${this.selectedUsers.length})`
    },

    canGenerate() {
      return this.selectedUsers.length > 0 && !this.loading && !this.generating
    },

    apiUrl() {
      const params = new URLSearchParams(location.search)
      const client = params.get('client')
      
      let url = `${this.urlstudents}?withUsers=true&onlyClient=true&userExist=false`
      url += `&page=${this.pagination.page}&per_page=${this.pagination.itemsPerPage}`
      
      // Add filters
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value.trim()) {
          url += `&${key}=${encodeURIComponent(value)}`
        }
      })
      
      return url
    }
  },

  watch: {
    'pagination.page'() {
      this.loadUsers()
    }
  },

  mounted() {
    this.loadUsers()
  },

  methods: {
    async loadUsers() {
      this.loading = true
      
      try {
        const response = await axios.get(this.apiUrl)
        
        this.users = response.data.data || []
        this.pagination.totalItems = response.data.total || 0
        this.pagination.page = response.data.current_page || 1
        this.pagination.pageCount = response.data.last_page || 0
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        this.loading = false
      }
    },

    async handleGenerate() {
      if (!this.canGenerate) return

      this.generating = true

      try {
        const studentIds = this.selectedUsers.map(user => 
          typeof user === 'object' ? user.id : user
        )

        const response = await axios.post('/dashboard/users/generate-users', {
          students: studentIds
        })

        // Reset generating state
        this.generating = false

        // Close modal first
        this.$emit('close')
        this.$emit('reloadtable', true)
        
        // Wait for modal to close, then show success message
        setTimeout(async () => {
          const message = response.data.message || 'Usuarios generados exitosamente'
          
          await this.$swal({
            icon: 'success',
            title: '¡Éxito!',
            text: message,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#1A0054',
            background: '#ffffff',
            color: '#1A0054'
          })
        }, 300)
      } catch (error) {
        console.error('Error generating users:', error)
        
        // Reset generating state before showing error
        this.generating = false
        
        // Show error message
        const errorMessage = error.response?.data?.message || 'Error al generar usuarios'
        
        await this.$swal({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1A0054',
          background: '#ffffff',
          color: '#1A0054'
        })
      }
    },

    handleSelectionUpdate(selection) {
      this.selectedUsers = selection
    },

    handlePageChange(newPage) {
      this.pagination.page = newPage
    },

    handleFilter() {
      this.pagination.page = 1
      this.selectedUsers = []
      this.loadUsers()
    },

    handleClearFilters() {
      this.filters = {
        firstname: '',
        lastname: '',
        email: '',
        position: '',
        area: '',
        fecha: ''
      }
      this.pagination.page = 1
      this.selectedUsers = []
      this.loadUsers()
    },

    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  width: 100%;
  max-width: 900px;
  margin: 0 20px;
}

.modal-dialog-scrollable .modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
</style>